import { useState, useEffect } from 'react'
import {
  Plus,
  Search,
  Edit2,
  Trash2,
  Webhook,
  Activity,
  CheckCircle2,
  XCircle,
  Clock,
  MoreHorizontal,
  X,
  AlertCircle,
  RefreshCw,
  Play,
  Pause,
  Eye,
  Copy,
  Key,
  Send,
  ChevronDown,
  ChevronUp,
  Filter,
  Zap,
  Globe,
  Shield,
  BarChart3,
  ArrowRight,
  ExternalLink
} from 'lucide-react'
import { api } from '../services/api'
import { Select } from '../components/ui/Select'

// Types
interface WebhookEvent {
  type: string
  category: string
  description: string
}

interface Webhook {
  id: string
  partnerId: string
  name: string
  url: string
  secret: string
  events: string[]
  isActive: boolean
  headers: Record<string, string>
  retryCount: number
  timeoutMs: number
  lastTriggeredAt: string | null
  lastStatus: string | null
  lastError: string | null
  successCount: number
  failureCount: number
  createdAt: string
  updatedAt: string
}

interface WebhookLog {
  id: string
  webhookId: string
  eventType: string
  payload: Record<string, unknown>
  responseStatus: number | null
  responseBody: string | null
  responseTimeMs: number | null
  attempt: number
  success: boolean
  error: string | null
  createdAt: string
}

interface WebhookStatistics {
  last24h: {
    total: number
    success: number
    failed: number
    avgResponseTime: number
  }
  last7d: {
    total: number
    success: number
    failed: number
    avgResponseTime: number
  }
  byEventType: Array<{
    eventType: string
    total: number
    success: number
    failed: number
  }>
  recentFailures: Array<{
    id: string
    eventType: string
    error: string
    createdAt: string
  }>
}

// API calls
const webhookApi = {
  getWebhooks: async (active?: boolean) => {
    const params = active !== undefined ? `?active=${active}` : ''
    const response = await api.get(`/partner-admin/webhooks${params}`)
    return response.data
  },
  getWebhook: async (id: string) => {
    const response = await api.get(`/partner-admin/webhooks/${id}`)
    return response.data
  },
  getEventTypes: async (): Promise<WebhookEvent[]> => {
    const response = await api.get('/partner-admin/webhooks/events')
    return response.data
  },
  getStatistics: async (webhookId?: string): Promise<WebhookStatistics> => {
    const params = webhookId ? `?webhookId=${webhookId}` : ''
    const response = await api.get(`/partner-admin/webhooks/statistics${params}`)
    return response.data
  },
  getLogs: async (webhookId: string, options?: { limit?: number; offset?: number; success?: boolean }) => {
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.offset) params.append('offset', options.offset.toString())
    if (options?.success !== undefined) params.append('success', options.success.toString())
    const response = await api.get(`/partner-admin/webhooks/${webhookId}/logs?${params}`)
    return response.data
  },
  createWebhook: async (data: { name: string; url: string; events: string[]; headers?: Record<string, string> }) => {
    const response = await api.post('/partner-admin/webhooks', data)
    return response.data
  },
  updateWebhook: async (id: string, data: Partial<Webhook>) => {
    const response = await api.put(`/partner-admin/webhooks/${id}`, data)
    return response.data
  },
  deleteWebhook: async (id: string) => {
    const response = await api.delete(`/partner-admin/webhooks/${id}`)
    return response.data
  },
  toggleWebhook: async (id: string) => {
    const response = await api.post(`/partner-admin/webhooks/${id}/toggle`)
    return response.data
  },
  regenerateSecret: async (id: string) => {
    const response = await api.post(`/partner-admin/webhooks/${id}/regenerate-secret`)
    return response.data
  },
  testWebhook: async (id: string) => {
    const response = await api.post(`/partner-admin/webhooks/${id}/test`)
    return response.data
  }
}

export function Webhooks() {
  const [webhooks, setWebhooks] = useState<Webhook[]>([])
  const [eventTypes, setEventTypes] = useState<WebhookEvent[]>([])
  const [statistics, setStatistics] = useState<WebhookStatistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterActive, setFilterActive] = useState<string>('all')
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showLogsModal, setShowLogsModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showSecretModal, setShowSecretModal] = useState(false)
  const [selectedWebhook, setSelectedWebhook] = useState<Webhook | null>(null)
  const [webhookLogs, setWebhookLogs] = useState<WebhookLog[]>([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    events: [] as string[],
    headers: {} as Record<string, string>
  })
  const [submitting, setSubmitting] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [copiedSecret, setCopiedSecret] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const [webhooksRes, eventsRes, statsRes] = await Promise.all([
        webhookApi.getWebhooks(filterActive === 'all' ? undefined : filterActive === 'active'),
        webhookApi.getEventTypes(),
        webhookApi.getStatistics()
      ])
      setWebhooks(webhooksRes.webhooks || [])
      setEventTypes(eventsRes)
      setStatistics(statsRes)
    } catch (err: any) {
      console.error('Error fetching webhooks:', err)
      setError('Erro ao carregar webhooks')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [filterActive])

  const handleAddWebhook = async () => {
    try {
      setSubmitting(true)
      await webhookApi.createWebhook(formData)
      setShowAddModal(false)
      setFormData({ name: '', url: '', events: [], headers: {} })
      fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao criar webhook')
    } finally {
      setSubmitting(false)
    }
  }

  const handleEditWebhook = async () => {
    if (!selectedWebhook) return
    try {
      setSubmitting(true)
      await webhookApi.updateWebhook(selectedWebhook.id, formData)
      setShowEditModal(false)
      setSelectedWebhook(null)
      fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao atualizar webhook')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteWebhook = async () => {
    if (!selectedWebhook) return
    try {
      setSubmitting(true)
      await webhookApi.deleteWebhook(selectedWebhook.id)
      setShowDeleteConfirm(false)
      setSelectedWebhook(null)
      fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir webhook')
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggleWebhook = async (webhook: Webhook) => {
    try {
      await webhookApi.toggleWebhook(webhook.id)
      setOpenDropdown(null)
      fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao alterar status')
    }
  }

  const handleRegenerateSecret = async () => {
    if (!selectedWebhook) return
    try {
      const result = await webhookApi.regenerateSecret(selectedWebhook.id)
      setSelectedWebhook({ ...selectedWebhook, secret: result.secret })
      fetchData()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao regenerar secret')
    }
  }

  const handleTestWebhook = async (webhook: Webhook) => {
    try {
      setTestResult(null)
      const result = await webhookApi.testWebhook(webhook.id)
      setTestResult({
        success: result.success,
        message: result.success ? 'Webhook enviado com sucesso!' : `Falha: ${result.error || 'Erro desconhecido'}`
      })
      fetchData()
    } catch (err: any) {
      setTestResult({
        success: false,
        message: err.response?.data?.message || 'Erro ao testar webhook'
      })
    }
  }

  const handleViewLogs = async (webhook: Webhook) => {
    setSelectedWebhook(webhook)
    setShowLogsModal(true)
    setLogsLoading(true)
    try {
      const result = await webhookApi.getLogs(webhook.id, { limit: 50 })
      setWebhookLogs(result.logs || [])
    } catch (err: any) {
      console.error('Error fetching logs:', err)
    } finally {
      setLogsLoading(false)
    }
  }

  const openEditModal = (webhook: Webhook) => {
    setSelectedWebhook(webhook)
    setFormData({
      name: webhook.name,
      url: webhook.url,
      events: webhook.events,
      headers: webhook.headers || {}
    })
    setShowEditModal(true)
    setOpenDropdown(null)
  }

  const openSecretModal = (webhook: Webhook) => {
    setSelectedWebhook(webhook)
    setShowSecretModal(true)
    setOpenDropdown(null)
  }

  const openDeleteConfirm = (webhook: Webhook) => {
    setSelectedWebhook(webhook)
    setShowDeleteConfirm(true)
    setOpenDropdown(null)
  }

  const copySecret = async (secret: string) => {
    await navigator.clipboard.writeText(secret)
    setCopiedSecret(true)
    setTimeout(() => setCopiedSecret(false), 2000)
  }

  const toggleEvent = (eventType: string) => {
    setFormData(prev => ({
      ...prev,
      events: prev.events.includes(eventType)
        ? prev.events.filter(e => e !== eventType)
        : [...prev.events, eventType]
    }))
  }

  const getEventCategory = (eventType: string) => {
    const event = eventTypes.find(e => e.type === eventType)
    return event?.category || 'Outros'
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const filteredWebhooks = webhooks.filter(webhook => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      return (
        webhook.name.toLowerCase().includes(search) ||
        webhook.url.toLowerCase().includes(search)
      )
    }
    return true
  })

  const groupedEventTypes = eventTypes.reduce((acc, event) => {
    if (!acc[event.category]) acc[event.category] = []
    acc[event.category].push(event)
    return acc
  }, {} as Record<string, WebhookEvent[]>)

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-white border-t-transparent"></div>
          </div>
          <p className="text-gray-500 font-medium">Carregando webhooks...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 bg-clip-text text-transparent">
                Webhooks
              </h1>
              <p className="mt-1 text-gray-500">
                Configure notificações automáticas para eventos do sistema
              </p>
            </div>
            <button
              onClick={() => {
                setFormData({ name: '', url: '', events: [], headers: {} })
                setShowAddModal(true)
              }}
              className="group relative inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-medium rounded-xl shadow-lg shadow-violet-500/25 hover:shadow-xl hover:shadow-violet-500/30 transition-all duration-200 hover:-translate-y-0.5"
            >
              <Plus className="w-4 h-4" />
              <span>Novo Webhook</span>
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3">
            <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <span className="flex-1 text-sm font-medium">{error}</span>
            <button onClick={() => setError(null)} className="p-1 hover:bg-red-100 rounded-lg transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Test Result */}
        {testResult && (
          <div className={`mb-6 ${testResult.success ? 'bg-emerald-50 border-emerald-100 text-emerald-700' : 'bg-red-50 border-red-100 text-red-700'} border px-4 py-3 rounded-xl flex items-center gap-3`}>
            <div className={`flex-shrink-0 w-10 h-10 ${testResult.success ? 'bg-emerald-100' : 'bg-red-100'} rounded-full flex items-center justify-center`}>
              {testResult.success ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
            </div>
            <span className="flex-1 text-sm font-medium">{testResult.message}</span>
            <button onClick={() => setTestResult(null)} className={`p-1 ${testResult.success ? 'hover:bg-emerald-100' : 'hover:bg-red-100'} rounded-lg transition-colors`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Stats Cards */}
        {statistics && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
            <div className="group relative bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-violet-500/10 to-transparent rounded-bl-[100px]"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/20">
                    <Webhook className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{webhooks.length}</p>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Total de webhooks</p>
              </div>
            </div>

            <div className="group relative bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-emerald-500/10 to-transparent rounded-bl-[100px]"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                    <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{statistics.last24h.success}</p>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Entregas (24h)</p>
              </div>
            </div>

            <div className="group relative bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/10 to-transparent rounded-bl-[100px]"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-rose-500 rounded-xl flex items-center justify-center shadow-lg shadow-red-500/20">
                    <XCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{statistics.last24h.failed}</p>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Falhas (24h)</p>
              </div>
            </div>

            <div className="group relative bg-white rounded-2xl p-4 sm:p-5 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-bl-[100px]"></div>
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20">
                    <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-gray-900">{statistics.last24h.avgResponseTime}ms</p>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Tempo médio</p>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar por nome ou URL..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border-0 rounded-xl text-sm placeholder-gray-400 focus:bg-white focus:ring-2 focus:ring-violet-500/20 transition-all"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-2 sm:gap-3">
              <Select
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}
                options={[
                  { value: 'all', label: 'Todos os status' },
                  { value: 'active', label: 'Ativos' },
                  { value: 'inactive', label: 'Inativos' }
                ]}
              />
              <button
                onClick={fetchData}
                className="p-2.5 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-500 hover:text-gray-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Webhooks List */}
        <div className="space-y-4">
          {filteredWebhooks.map((webhook) => {
            const successRate = webhook.successCount + webhook.failureCount > 0
              ? Math.round((webhook.successCount / (webhook.successCount + webhook.failureCount)) * 100)
              : 100

            return (
              <div
                key={webhook.id}
                className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
              >
                <div className="p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                    {/* Icon and Status */}
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
                        webhook.isActive
                          ? 'bg-gradient-to-br from-violet-500 to-purple-600 shadow-violet-500/20'
                          : 'bg-gradient-to-br from-gray-400 to-gray-500 shadow-gray-400/20'
                      }`}>
                        <Webhook className="w-6 h-6 text-white" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 truncate">{webhook.name}</h3>
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            webhook.isActive
                              ? 'bg-emerald-50 text-emerald-700'
                              : 'bg-gray-100 text-gray-600'
                          }`}>
                            <div className={`w-1.5 h-1.5 rounded-full ${webhook.isActive ? 'bg-emerald-500' : 'bg-gray-400'}`}></div>
                            {webhook.isActive ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>

                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                          <Globe className="w-4 h-4" />
                          <span className="truncate">{webhook.url}</span>
                        </div>

                        {/* Events Tags */}
                        <div className="flex flex-wrap gap-1.5">
                          {webhook.events.slice(0, 4).map((event) => (
                            <span
                              key={event}
                              className="inline-flex items-center px-2 py-0.5 rounded-lg bg-violet-50 text-violet-700 text-xs font-medium"
                            >
                              {event}
                            </span>
                          ))}
                          {webhook.events.length > 4 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-medium">
                              +{webhook.events.length - 4} mais
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Stats and Actions */}
                    <div className="flex items-center gap-4 sm:gap-6">
                      {/* Stats */}
                      <div className="hidden sm:flex items-center gap-6">
                        <div className="text-center">
                          <div className="flex items-center gap-1">
                            <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            <span className="text-lg font-bold text-gray-900">{webhook.successCount}</span>
                          </div>
                          <p className="text-xs text-gray-500">Sucessos</p>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center gap-1">
                            <XCircle className="w-4 h-4 text-red-500" />
                            <span className="text-lg font-bold text-gray-900">{webhook.failureCount}</span>
                          </div>
                          <p className="text-xs text-gray-500">Falhas</p>
                        </div>
                        <div className="text-center">
                          <div className="flex items-center gap-1">
                            <BarChart3 className="w-4 h-4 text-blue-500" />
                            <span className="text-lg font-bold text-gray-900">{successRate}%</span>
                          </div>
                          <p className="text-xs text-gray-500">Taxa sucesso</p>
                        </div>
                      </div>

                      {/* Quick Actions */}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleTestWebhook(webhook)}
                          className="p-2 bg-violet-50 hover:bg-violet-100 text-violet-600 rounded-lg transition-colors"
                          title="Testar webhook"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleViewLogs(webhook)}
                          className="p-2 bg-gray-50 hover:bg-gray-100 text-gray-600 rounded-lg transition-colors"
                          title="Ver logs"
                        >
                          <Eye className="w-4 h-4" />
                        </button>

                        {/* Dropdown Menu */}
                        <div className="relative">
                          <button
                            onClick={() => setOpenDropdown(openDropdown === webhook.id ? null : webhook.id)}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <MoreHorizontal className="w-4 h-4 text-gray-500" />
                          </button>
                          {openDropdown === webhook.id && (
                            <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-20">
                              <button
                                onClick={() => openEditModal(webhook)}
                                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 transition-colors"
                              >
                                <Edit2 className="w-4 h-4 text-gray-400" /> Editar
                              </button>
                              <button
                                onClick={() => openSecretModal(webhook)}
                                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 transition-colors"
                              >
                                <Key className="w-4 h-4 text-gray-400" /> Ver Secret
                              </button>
                              <button
                                onClick={() => handleToggleWebhook(webhook)}
                                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2.5 transition-colors"
                              >
                                {webhook.isActive ? (
                                  <><Pause className="w-4 h-4 text-gray-400" /> Desativar</>
                                ) : (
                                  <><Play className="w-4 h-4 text-gray-400" /> Ativar</>
                                )}
                              </button>
                              <div className="border-t border-gray-100"></div>
                              <button
                                onClick={() => openDeleteConfirm(webhook)}
                                className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" /> Excluir
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Last Triggered */}
                  {webhook.lastTriggeredAt && (
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        Último disparo: {formatDate(webhook.lastTriggeredAt)}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        webhook.lastStatus === 'success'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-red-50 text-red-700'
                      }`}>
                        {webhook.lastStatus === 'success' ? (
                          <><CheckCircle2 className="w-3 h-3" /> Sucesso</>
                        ) : (
                          <><XCircle className="w-3 h-3" /> Falha</>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>

        {/* Empty State */}
        {filteredWebhooks.length === 0 && (
          <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-100 to-purple-100 mb-4">
              <Webhook className="w-8 h-8 text-violet-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Nenhum webhook configurado</h3>
            <p className="text-gray-500 mb-6">Configure webhooks para receber notificações automáticas.</p>
            <button
              onClick={() => {
                setFormData({ name: '', url: '', events: [], headers: {} })
                setShowAddModal(true)
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 text-white font-medium rounded-xl shadow-lg shadow-violet-500/25"
            >
              <Plus className="w-4 h-4" />
              Criar Webhook
            </button>
          </div>
        )}
      </div>

      {/* Click outside to close dropdown */}
      {openDropdown && <div className="fixed inset-0 z-10" onClick={() => setOpenDropdown(null)} />}

      {/* Add/Edit Webhook Modal */}
      {(showAddModal || showEditModal) && (
        <Modal
          onClose={() => {
            setShowAddModal(false)
            setShowEditModal(false)
            setSelectedWebhook(null)
          }}
          title={showAddModal ? 'Novo Webhook' : 'Editar Webhook'}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Nome</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 border-0 rounded-xl text-sm focus:ring-2 focus:ring-violet-500/20 focus:bg-white transition-all"
                placeholder="Ex: Notificações de clientes"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">URL do Endpoint</label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="w-full px-4 py-2.5 bg-gray-50 border-0 rounded-xl text-sm focus:ring-2 focus:ring-violet-500/20 focus:bg-white transition-all"
                placeholder="https://seu-servidor.com/webhook"
              />
            </div>

            {/* Events Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Eventos</label>
              <div className="max-h-64 overflow-y-auto space-y-3 bg-gray-50 rounded-xl p-3">
                {Object.entries(groupedEventTypes).map(([category, events]) => (
                  <div key={category}>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{category}</p>
                    <div className="space-y-1">
                      {events.map((event) => (
                        <label
                          key={event.type}
                          className="flex items-center gap-3 p-2 rounded-lg hover:bg-white cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={formData.events.includes(event.type)}
                            onChange={() => toggleEvent(event.type)}
                            className="w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900">{event.type}</p>
                            <p className="text-xs text-gray-500 truncate">{event.description}</p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {formData.events.length} evento(s) selecionado(s)
              </p>
            </div>
          </div>
          <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
            <button
              onClick={() => {
                setShowAddModal(false)
                setShowEditModal(false)
                setSelectedWebhook(null)
              }}
              className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={showAddModal ? handleAddWebhook : handleEditWebhook}
              disabled={submitting || !formData.name || !formData.url || formData.events.length === 0}
              className="flex-1 py-2.5 px-4 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-medium rounded-xl shadow-lg shadow-violet-500/25 disabled:opacity-50 disabled:shadow-none transition-all"
            >
              {submitting ? 'Salvando...' : showAddModal ? 'Criar' : 'Salvar'}
            </button>
          </div>
        </Modal>
      )}

      {/* Secret Modal */}
      {showSecretModal && selectedWebhook && (
        <Modal
          onClose={() => {
            setShowSecretModal(false)
            setSelectedWebhook(null)
          }}
          title="Webhook Secret"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Use este secret para verificar a autenticidade das requisições recebidas.
            </p>

            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between gap-3">
                <code className="text-sm text-gray-800 font-mono break-all flex-1">
                  {selectedWebhook.secret}
                </code>
                <button
                  onClick={() => copySecret(selectedWebhook.secret)}
                  className={`p-2 rounded-lg transition-colors ${
                    copiedSecret ? 'bg-emerald-100 text-emerald-600' : 'bg-gray-200 hover:bg-gray-300 text-gray-600'
                  }`}
                >
                  {copiedSecret ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
              <strong>Atenção:</strong> Mantenha este secret seguro e nunca o exponha publicamente.
            </div>
          </div>

          <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
            <button
              onClick={() => {
                setShowSecretModal(false)
                setSelectedWebhook(null)
              }}
              className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
            >
              Fechar
            </button>
            <button
              onClick={handleRegenerateSecret}
              className="flex-1 py-2.5 px-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium rounded-xl shadow-lg shadow-amber-500/25 transition-all"
            >
              Regenerar Secret
            </button>
          </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedWebhook && (
        <Modal onClose={() => { setShowDeleteConfirm(false); setSelectedWebhook(null) }} title="">
          <div className="text-center py-4">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-100 mb-4">
              <Trash2 className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Excluir Webhook</h3>
            <p className="text-gray-500 mb-4">
              Tem certeza que deseja excluir <strong className="text-gray-900">{selectedWebhook.name}</strong>?
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800 mb-6">
              Esta ação não pode ser desfeita. Todos os logs serão perdidos.
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteConfirm(false); setSelectedWebhook(null) }}
                className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteWebhook}
                disabled={submitting}
                className="flex-1 py-2.5 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50"
              >
                {submitting ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Logs Modal */}
      {showLogsModal && selectedWebhook && (
        <Modal
          onClose={() => {
            setShowLogsModal(false)
            setSelectedWebhook(null)
            setWebhookLogs([])
          }}
          title={`Logs - ${selectedWebhook.name}`}
          size="large"
        >
          {logsLoading ? (
            <div className="py-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-violet-100 mb-4">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-violet-600 border-t-transparent"></div>
              </div>
              <p className="text-gray-500">Carregando logs...</p>
            </div>
          ) : webhookLogs.length === 0 ? (
            <div className="py-12 text-center">
              <Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Nenhum log encontrado</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[60vh] overflow-y-auto">
              {webhookLogs.map((log) => (
                <div
                  key={log.id}
                  className={`p-4 rounded-xl border ${
                    log.success
                      ? 'bg-emerald-50/50 border-emerald-100'
                      : 'bg-red-50/50 border-red-100'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      {log.success ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <div>
                        <span className="font-medium text-gray-900">{log.eventType}</span>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {formatDate(log.createdAt)} • Tentativa {log.attempt}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {log.responseStatus && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          log.responseStatus >= 200 && log.responseStatus < 300
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          HTTP {log.responseStatus}
                        </span>
                      )}
                      {log.responseTimeMs && (
                        <p className="text-xs text-gray-500 mt-1">{log.responseTimeMs}ms</p>
                      )}
                    </div>
                  </div>
                  {log.error && (
                    <div className="mt-2 p-2 bg-red-100 rounded-lg text-xs text-red-700">
                      {log.error}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-3 mt-6 pt-4 border-t border-gray-100">
            <button
              onClick={() => {
                setShowLogsModal(false)
                setSelectedWebhook(null)
                setWebhookLogs([])
              }}
              className="flex-1 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
            >
              Fechar
            </button>
          </div>
        </Modal>
      )}
    </div>
  )
}

// Modal Component
function Modal({
  children,
  onClose,
  title,
  size = 'default'
}: {
  children: React.ReactNode
  onClose: () => void
  title: string
  size?: 'default' | 'large'
}) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className={`bg-white w-full ${size === 'large' ? 'sm:max-w-2xl' : 'sm:max-w-md'} sm:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-hidden shadow-2xl animate-in slide-in-from-bottom sm:slide-in-from-bottom-0 sm:zoom-in-95 duration-200`}>
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        )}
        <div className="px-6 py-4 overflow-y-auto max-h-[70vh]">
          {children}
        </div>
      </div>
    </div>
  )
}
